/** @jsx jsx */
import { jsx } from "theme-ui"
import { useEffect, useContext, useRef, useCallback, useState } from "react"
import { gsap } from "gsap"
import Img from "gatsby-image"
import { keyframes } from "@emotion/core"

import Link from "../../../atoms/Link"
import createMarker from "./createMarker"
import context from "./context"
import threeProps from "./threeProps"
import { toScreenPosition } from "./helpers"

// static marker animation
const markerOutsideScale = {
  value: 0.8,
}
const markerInsideScale = {
  value: 1,
}

const popupSize = {
  w: 340,
  h: 65,
}

const fadeIn = keyframes`
  0% {
    transform: translateY(10px);
    opacity: 0
  }
  100% {
    transform: translateY(0);
    opacity: 1
  }
`

const fadeOut = keyframes`
  0% {
    transform: translateY(0);
    opacity: 1
  }
  100% {
    transform: translateY(10px);
    opacity: 0
  }
`

const Marker = ({ lat, lon, to, headline, title, image, type }) => {
  const {
    camera,
    renderer,
    intersects,
    markers,
    onMarkerMount,
    onMarkerUnmount,
  } = useContext(context)
  const [isVisible, setVisible] = useState(false)
  const [isSelected, setSelected] = useState(false)
  const instance = useRef()
  const elRef = useRef()
  const intersect = useRef(false)
  const isOverPopup = useRef(false)
  const isSelectedRef = useRef(false)
  const stackingIndex = useRef(0)

  intersect.current = intersects.find(
    intersect => intersect.object.parent === instance.current.object
  )

  const intersectingMarkers =
    intersect &&
    markers.filter(marker =>
      intersects.find(intersect => marker.object === intersect.object.parent)
    )

  const handleMouseEnter = useCallback(() => {
    isOverPopup.current = true
  }, [])

  const handleMouseLeave = useCallback(() => {
    isOverPopup.current = false
  }, [])

  // Set marker animation
  useEffect(() => {
    const scaleOutsideAnimation = gsap.to(markerOutsideScale, {
      value: 1.5,
      duration: 3,
      ease: "power2.inOut",
      repeat: -1,
      yoyo: true,
    })

    const scaleInside = gsap.to(markerInsideScale, {
      value: 1.5,
      duration: 3,
      delay: 1,
      ease: "power2.inOut",
      repeat: -1,
      yoyo: true,
    })

    return () => {
      scaleOutsideAnimation.kill()
      scaleInside.kill()
    }
  }, [])

  // Add markers
  useEffect(() => {
    instance.current = createMarker(lat, lon, threeProps, type)

    const animate = () => {
      instance.current.outside.scale.set(
        markerOutsideScale.value,
        markerOutsideScale.value,
        markerOutsideScale.value
      )

      instance.current.inside.scale.set(
        markerInsideScale.value,
        0.1,
        markerInsideScale.value
      )

      if (isSelectedRef.current) {
        const pinPos = toScreenPosition(
          instance.current.object,
          camera,
          renderer
        )

        elRef.current.style.transform = `translate3d(${pinPos.x -
          popupSize.w / 2}px, ${pinPos.y - popupSize.h}px, 0)`
      }
    }

    instance.current.render = animate

    onMarkerMount(instance.current)

    return () => {
      onMarkerUnmount(instance.current)
    }
  }, [lat, lon, camera, renderer, type, onMarkerMount, onMarkerUnmount])

  useEffect(() => {
    let timeout = null

    const isSelected = !!(
      (instance.current && intersect.current) ||
      isOverPopup.current
    )

    if (isSelected) {
      setVisible(isSelected)
      setSelected(isSelected)
      isSelectedRef.current = isSelected
      const newStackingIndex =
        intersectingMarkers &&
        Math.max(intersectingMarkers.indexOf(instance.current), 0)
      if (stackingIndex.current !== newStackingIndex) {
        setSelected(false)
        isSelectedRef.current = false
        stackingIndex.current = 0
      }
      stackingIndex.current = newStackingIndex
    } else {
      setVisible(isSelected)

      timeout = setTimeout(() => {
        setSelected(false)
        isSelectedRef.current = false
        stackingIndex.current = 0
      }, 200)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [intersects, intersectingMarkers, isOverPopup])

  return isSelected ? (
    <div
      ref={elRef}
      sx={{
        position: "absolute",
        left: 0,
        "&:after": {
          display: "block",
          position: "absolute",
          content: "' '",
          width: stackingIndex.current ? "100%" : 30,
          height: 30,
          mx: "auto",
          left: 0,
          right: 0,
        },
      }}
      style={{ top: (-popupSize.h - 15) * stackingIndex.current - 30 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* logic to check if the content passed to the marker is a PROJECT or a LIVE POST,
      and then create the coresponding element for each */}
      {image.src ? (
        // if it is a PROJECT
        <Link
          to={to}
          sx={{
            display: "flex",
            boxShadow: "cardSmall",
            bg: "background",
            borderRadius: "default",
            overflow: "hidden",
            alignItems: "center",
            width: popupSize.w,
            animation: isVisible
              ? `${fadeIn} 200ms 1 ease-out both`
              : `${fadeOut} 200ms 1 ease-out both`,
          }}
        >
          <Img
            sx={{
              display: "block",
              flexShrink: 0,
            }}
            fixed={image}
            alt={title}
          />
          <h4
            sx={{
              fontWeight: "heading",
              mx: 2,
              my: 0,
              fontSize: 2,
              lineHeight: "heading",
            }}
          >
            <span
              sx={{
                fontWeight: "bold",
                fontSize: 1,
                pt: 0,
                pb: 0,
                lineHeight: "heading",
                color: "primary",
                textTransform: "uppercase",
                display: headline ? "block" : "inline",
              }}
            >
              {headline}
            </span>{" "}
            <span
              sx={{
                my: -6,
              }}
            >
              {" "}
              {title}
            </span>
          </h4>
        </Link>
      ) : (
        // if it is a LIVE POST
        <a
          href={to}
          sx={{
            display: "flex",
            boxShadow: "cardSmall",
            bg: "background",
            borderRadius: "default",
            overflow: "hidden",
            alignItems: "center",
            width: popupSize.w,
            animation: isVisible
              ? `${fadeIn} 200ms 1 ease-out both`
              : `${fadeOut} 200ms 1 ease-out both`,
          }}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div
            sx={{
              display: "block",
              backgroundSize: "cover",
              backgroundPosition: "center top",
              backgroundRepeat: "no-repeat",
              paddingBottom: 65,
              width: 100,
              minWidth: 100,
              flexDirection: "column",
            }}
            style={{ backgroundImage: `url(${image})` }}
          />
          <h4
            sx={{
              fontWeight: "heading",
              mx: 2,
              my: 0,
              fontSize: 2,
              lineHeight: "heading",
            }}
          >
            <span
              sx={{
                fontWeight: "bold",
                fontSize: 1,
                pt: 0,
                pb: 0,
                lineHeight: "heading",
                color: "primary",
                textTransform: "uppercase",
                display: "block",
              }}
            >
              {headline}
            </span>{" "}
            <span
              sx={{
                my: -6,
              }}
            >
              {" "}
              {title}
            </span>
          </h4>
        </a>
      )}
    </div>
  ) : null
}

export default Marker
